import { Link } from 'react-router-dom';

import { IconClose } from '../../../common/icons';
import { FUNNEL_LABELS, FUNNEL_COLORS, RATE_ROWS } from '../../../core/constants/kpis';
import { fmtNum, fmtPct } from '../../kpis/utils/format';
import ClientFarmSearch from '../../clients/forms/search';

import '../../kpis/layouts/styles/dashboard.css';
import './styles/entity-stats.css';

// Vista de estadísticas por entidad (admin-stats.md). La forma difiere del dashboard
// /kpis: el funnel trae solo {key,total,rate_vs_previous} (sin `display`) → barras
// horizontales propias; las tasas reusan el estilo del dashboard pero SOLO las que
// vienen (cada nivel tiene un set distinto). Producción según las claves presentes.

const todayStr = () => new Date().toLocaleDateString('en-CA');

const PROD_ORDER = [
    ['opu_sessions', 'Sesiones OPU'], ['aspirations', 'Aspiraciones'], ['oocytes', 'Óvocitos'], ['viables', 'Viables'],
    ['clivages', 'Clivajes'], ['embryos', 'Embriones'], ['transfers', 'Transferencias'], ['pregnancies', 'Preñadas'],
    ['confirmed', 'Confirmadas'], ['lost', 'Vacías'], ['born', 'Partos'],
    ['births', 'Partos'], ['crias', 'Crías'], ['losses', 'Pérdidas'],
];

const SEX_SHORT = { female: 'H', male: 'M' };

// Barra de filtros: rango de fechas + (opcional) cliente/finca compuesto.
export function StatsFilters({ range, onRange, withScope, active, onPick, onClear, busy }) {
    return (
        <div className="estats__filters">
            {withScope && (active ? (
                <div className="estats-active">
                    <span className="estats-active__label">{active.type === 'farm' ? 'Finca' : 'Cliente'}</span>
                    <span className="estats-active__value">{active.label}</span>
                    <button type="button" className="estats-active__clear" onClick={onClear} aria-label="Quitar filtro"><IconClose /></button>
                </div>
            ) : (
                <div className="estats__search"><ClientFarmSearch scope="all" placeholder="Cliente o finca…" onSelect={onPick} /></div>
            ))}
            <label className="estats__date">
                <span className="estats__date-label">Desde</span>
                <input type="date" value={range.from} max={range.to || todayStr()} onChange={(e) => onRange({ from: e.target.value })} disabled={busy} />
            </label>
            <label className="estats__date">
                <span className="estats__date-label">Hasta</span>
                <input type="date" value={range.to} max={todayStr()} onChange={(e) => onRange({ to: e.target.value })} disabled={busy} />
            </label>
        </div>
    );
}

// Panel de crías (offspring/progeny) — misma forma { count, registered, items }.
export function OffspringPanel({ title, data }) {
    if (!data) return null;
    const items = data.items || [];
    return (
        <article className="report-card estats-extra">
            <h2 className="report-section__title">{title}</h2>
            <p className="estats-extra__count">{fmtNum(data.count)} cría(s) · {fmtNum(data.registered)} registradas</p>
            {items.length > 0 && (
                <ul className="estats-extra__list">
                    {items.map((c) => (
                        <li key={c.id} className="estats-extra__item">
                            <span className="estats-extra__name">{c.name || c.number || `#${c.id}`}</span>
                            {c.number && <span className="estats-extra__sub">{c.number}</span>}
                            {c.sex && <span className="estats-extra__sex">{SEX_SHORT[c.sex] || c.sex}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </article>
    );
}

// Bloques de una entidad: tarjetas de producción + embudo + tasas. Reutilizable —
// StatsView los envuelve con header/filtros; el stats por cliente los renderiza una vez
// por naturaleza (donadoras=cowhousing, receptoras=parcería).
export function StatsBlocks({ data }) {
    const production = data?.production || {};
    const stages = data?.funnel?.stages || [];
    const maxTotal = stages.reduce((m, s) => Math.max(m, Number(s.total) || 0), 0);
    const rateRows = RATE_ROWS.filter((row) => data?.rates && row.key in data.rates);
    const prodCards = PROD_ORDER.filter(([k]) => production[k] != null);

    return (
        <>
            {prodCards.length > 0 && (
                <div className="report-totals">
                    {prodCards.map(([key, label]) => (
                        <article key={key} className="report-total-card">
                            <span className="report-total-card__body">
                                <span className="report-total-card__value">{fmtNum(production[key])}</span>
                                <span className="report-total-card__label">{label}</span>
                            </span>
                            <span className="report-total-card__bar" />
                        </article>
                    ))}
                </div>
            )}

            <div className="estats__grid">
                <article className="report-card estats-funnel">
                    <h2 className="report-section__title">Embudo</h2>
                    <ul className="estats-funnel__list">
                        {stages.map((s) => {
                            const w = maxTotal ? Math.round(((Number(s.total) || 0) / maxTotal) * 100) : 0;
                            return (
                                <li key={s.key} className="estats-funnel__row">
                                    <span className="estats-funnel__label">{FUNNEL_LABELS[s.key] || s.key}</span>
                                    <span className="estats-funnel__bar"><span className="estats-funnel__fill" style={{ width: `${w}%`, background: FUNNEL_COLORS[s.key] || '#94a3b8' }} /></span>
                                    <span className="estats-funnel__total">{fmtNum(s.total)}</span>
                                    <span className="estats-funnel__rate">{s.rate_vs_previous == null ? '—' : fmtPct(s.rate_vs_previous)}</span>
                                </li>
                            );
                        })}
                        {!stages.length && <li className="estats-funnel__empty">Sin datos.</li>}
                    </ul>
                </article>

                <article className="report-card report-rates">
                    <h2 className="report-section__title">Tasas del proceso</h2>
                    <div className="report-rates__grid">
                        {rateRows.map((row) => {
                            const r = data.rates[row.key] || {};
                            const width = Math.max(0, Math.min(100, Number(r.value) || 0));
                            return (
                                <div key={row.key} className="report-rate" data-qualifier={r.qualifier || 'none'}>
                                    <div className="report-rate__head"><span className="report-rate__label">{row.label}</span><span className="report-rate__value">{fmtPct(r.value)}</span></div>
                                    <div className="report-rate__track"><span className="report-rate__fill" style={{ width: `${width}%` }} /></div>
                                    <div className="report-rate__sub"><span>{fmtNum(r.numerator)} / {fmtNum(r.denominator)}</span>{row.late && r.cohort_n != null && <span>cohorte {fmtNum(r.cohort_n)}</span>}</div>
                                </div>
                            );
                        })}
                        {!rateRows.length && <p className="estats__muted">Sin tasas.</p>}
                    </div>
                </article>
            </div>
        </>
    );
}

export default function StatsView({ data, title, subtitle, backTo, backLabel, isLoading, isFetching, error, filters, children }) {
    return (
        <div className={`estats ${isFetching ? 'estats--busy' : ''}`}>
            <header className="estats__header">
                {backTo && <Link to={backTo} className="estats__back">{backLabel || 'Volver'}</Link>}
                {title && <h1 className="estats__title">{title}</h1>}
                {subtitle && <p className="estats__subtitle">{subtitle}</p>}
            </header>

            {filters}

            {error && (
                <div className="estats__error">
                    {error.status === 403 ? 'No tenés permiso para ver las estadísticas.' : (error.message || 'No se pudieron cargar las estadísticas.')}
                </div>
            )}

            {isLoading && !data ? (
                <div className="estats__state">Cargando estadísticas…</div>
            ) : (
                <>
                    <StatsBlocks data={data} />
                    {children}
                </>
            )}
        </div>
    );
}
